import { CometChat } from '@cometchat-pro/chat'
import Storage from '@/services/Storage'
import { v4 as uuid } from 'uuid'
const log = require('bows')('chatStore', '[FE]')

export default {
  state: {
    chatID: Storage.getItem('chat-id') ? JSON.parse(Storage.getItem('chat-id')) : {},
    chatName: {},
    chatRole: {},
    CRIDs: [],
    chatActive: false,
    viewedGroupChat: false,
    incomingMessage: false,
    listenerID: null,
    latestMessage: null,
    alertMessages: [],
    notificationEnabled: true,
    isFetching: null,
    isChecking: false,
    error: null,
    version: '1.0'
  },
  getters: {
    chatActive: state => state.chatActive,
    chatID: state => state.chatID,
    chatName: state => state.chatName,
    chatRole: state => state.chatRole,
    CRIDs: state => state.CRIDs,
    incomingMessage: state => state.incomingMessage,
    listenerID: state => state.listenerID,
    latestMessage: state => state.latestMessage,
    alertMessages: state => state.alertMessages,
    notificationEnabled: state => state.notificationEnabled,
    viewedGroupChat: state => state.viewedGroupChat,
    chatError: state => state.error,
    chatIsChecking: state => state.isChecking
  },
  mutations: {
    CHAT_IS_FETCHING(state, value = true) {
      state.isFetching = value
      state.error = null
    },
    CHAT_IS_CHECKING(state, value = true) {
      state.isChecking = value
    },
    CHAT_SET_ID(state, chatData) {
      state.chatID = chatData
      Storage.setItem('chat-id', JSON.stringify(state.chatID))
    },
    CHAT_SET_NAME(state, chatData) {
      delete state.chatName[chatData.tour]
      state.chatName = { [chatData.tour]: chatData.name, ...state.chatName }
    },
    CHAT_SET_ROLE(state, chatData) {
      delete state.chatRole[chatData.tour]
      state.chatRole = { [chatData.tour]: chatData.data.role, ...state.chatRole }
    },
    CHAT_SET_VIEW_GROUP(state, viewed) {
      state.viewedGroupChat = viewed
    },
    CHAT_SET_NEW_INCOMING_MESSAGE(state, incoming) {
      state.incomingMessage = incoming
    },
    CHAT_SET_LISTENER_ID(state, id) {
      state.listenerID = id
    },
    CHAT_SET_LATEST_MESSAGE(state, message) {
      state.latestMessage = message
    },
    CHAT_ADD_ALERT_MESSAGE(state, message) {
      if (!state.alertMessages.find(msg => msg.id === message.id)) {
        state.alertMessages.push(message)
      }
    },
    CHAT_RESET_ALERT_MESSAGE(state) {
      state.alertMessages = []
    },
    CHAT_SET_NOTIFICATION_ENABLED(state, enabled) {
      state.notificationEnabled = enabled
    },
    CHAT_SET_ACTIVE(state, chatActive) {
      state.chatActive = chatActive
    },
    CHAT_FETCH_USER_SUCCESS(state, chatData) {
      state.isFetching = false
      state.error = null
      delete state.chatID[chatData.tour]
      delete state.chatName[chatData.tour]
      delete state.chatRole[chatData.tour]
      state.chatID = { [chatData.tour]: chatData.data.uid, ...state.chatID }
      state.chatName = { [chatData.tour]: chatData.data.name, ...state.chatName }
      state.chatRole = { [chatData.tour]: chatData.data.role, ...state.chatRole }
      if (chatData.data.CRIDs) {
        state.CRIDs = chatData.data.CRIDs
      }
      Storage.setItem('chat-id', JSON.stringify(state.chatID))
    },
    CHAT_LOGOUT(state, tour) {
      delete state.chatID[tour]
      delete state.chatName[tour]
      delete state.chatRole[tour]
    },
    CHAT_ERROR(state, error) {
      state.isFetching = false
      state.error = error
    }
  },
  actions: {
    CHAT_SET_ACTIVE({ commit }, chatActive = true) {
      commit('CHAT_SET_ACTIVE', chatActive)
    },
    CHAT_SET_VIEW_GROUP({ commit }, viewed = true) {
      commit('CHAT_SET_VIEW_GROUP', viewed)
    },
    CHAT_SET_NEW_INCOMING_MESSAGE({ commit }, incoming = true) {
      commit('CHAT_SET_NEW_INCOMING_MESSAGE', incoming)
    },
    CHAT_SET_LATEST_MESSAGE({ commit }, message) {
      commit('CHAT_SET_LATEST_MESSAGE', message)
    },
    CHAT_ADD_ALERT_MESSAGE({ commit }, message) {
      commit('CHAT_ADD_ALERT_MESSAGE', message)
    },
    CHAT_RESET_ALERT_MESSAGE({ commit }) {
      commit('CHAT_RESET_ALERT_MESSAGE')
    },
    CHAT_SET_NOTIFICATION_ENABLED({ commit }, enabled = true) {
      commit('CHAT_SET_NOTIFICATION_ENABLED', enabled)
    },
    CHAT_CHECK_LOCAL_STORAGE_VERSION({ commit, state }) {
      return new Promise(resolve => {
        commit('CHAT_IS_CHECKING')
        if (state.chatID['__VERSION__'] === state.version) {
          commit('CHAT_IS_CHECKING', false)
          resolve(true)
        } else {
          commit('CHAT_SET_ID', { __VERSION__: state.version })
          commit('CHAT_IS_CHECKING', false)
          resolve(false)
        }
      })
    },
    CHAT_ATTACH_LISTENER({ commit }, acceptGroup) {
      const generatedUUID = uuid()
      commit('CHAT_SET_LISTENER_ID', generatedUUID)
      CometChat.addMessageListener(
        generatedUUID,
        new CometChat.MessageListener({
          onTextMessageReceived: textMessage => {
            if (textMessage.receiverType !== 'group' || acceptGroup) {
              commit('CHAT_SET_NEW_INCOMING_MESSAGE', true)
            }
            commit('CHAT_SET_LATEST_MESSAGE', textMessage)
          }
        })
      )
    },
    CHAT_DETACH_LISTENER({ state }) {
      if (state.listenerID) {
        CometChat.removeMessageListener(state.listenerID)
      }
    },
    CHAT_INIT({ commit }, { region, appID }) {
      return new Promise((resolve, reject) => {
        try {
          commit('CHAT_IS_FETCHING')
          const appSetting = new CometChat.AppSettingsBuilder().subscribePresenceForAllUsers().setRegion(region).build()
          CometChat.init(appID, appSetting).then(
            () => {
              commit('CHAT_IS_FETCHING', false)
              resolve()
            },
            error => {
              commit('CHAT_ERROR', error)
              reject(error)
            }
          )
        } catch (error) {
          commit('CHAT_ERROR', error)
          reject(error)
        }
      })
    },
    CHAT_CREATE_NEW_GUEST_USER({ commit }, authKey) {
      return new Promise((resolve, reject) => {
        commit('CHAT_IS_FETCHING')
        const generatedUUID = uuid()
        let user = new CometChat.User(generatedUUID)
        user.setName(`Guest-${generatedUUID.slice(-5)}`)
        user.setRole('default')
        CometChat.createUser(user, authKey).then(
          user => {
            commit('CHAT_IS_FETCHING', false)
            resolve(user.uid)
          },
          error => {
            commit('CHAT_ERROR', error)
            reject(error)
          }
        )
      })
    },
    CHAT_LOGIN({ commit }, { tour, authKey, id, name, role = 'default' }) {
      return new Promise((resolve, reject) => {
        commit('CHAT_IS_FETCHING')
        CometChat.login(id, authKey).then(
          user => {
            commit('CHAT_FETCH_USER_SUCCESS', { data: user, tour })
            resolve(user)
          },
          error => {
            // if user not found, create one
            if (error.code === 'ERR_UID_NOT_FOUND') {
              let user = new CometChat.User(id)
              user.setName(name)
              user.setRole(role)
              log('Chat user not exists, creating new one')
              CometChat.createUser(user, authKey).then(
                () => {
                  // login after creating user
                  CometChat.login(id, authKey).then(
                    user => {
                      commit('CHAT_FETCH_USER_SUCCESS', { data: user, tour })
                      resolve(user)
                    },
                    error => {
                      commit('CHAT_ERROR', error)
                      reject(error)
                    }
                  )
                },
                error => {
                  commit('CHAT_ERROR', error)
                  reject(error)
                }
              )
            } else {
              commit('CHAT_ERROR', error)
              reject(error)
            }
          }
        )
      })
    },
    CHAT_LOGOUT({ commit }, tour) {
      return new Promise((resolve, reject) => {
        CometChat.logout().then(
          () => {
            commit('CHAT_LOGOUT', tour)
            resolve()
          },
          error => {
            reject(error)
          }
        )
      })
    },
    CHAT_GET_USER({ commit }, uid) {
      return new Promise((resolve, reject) => {
        commit('CHAT_IS_FETCHING', false)
        CometChat.getUser(uid).then(
          user => {
            resolve(user)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    CHAT_GET_GROUP({ commit }, guid) {
      return new Promise((resolve, reject) => {
        commit('CHAT_IS_FETCHING', false)
        CometChat.getGroup(guid).then(
          group => {
            resolve(group)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    CHAT_JOIN_PUBLIC_GROUP({ commit }, guid) {
      return new Promise((resolve, reject) => {
        let password = ''
        let groupType = CometChat.GROUP_TYPE.PUBLIC

        commit('CHAT_IS_FETCHING', false)
        CometChat.joinGroup(guid, groupType, password).then(
          group => {
            resolve(group)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    CHAT_GROUP_LEAVE(guid) {
      return new Promise((resolve, reject) => {
        CometChat.leaveGroup(guid).then(
          hasLeft => {
            resolve(hasLeft)
          },
          error => {
            reject(error)
          }
        )
      })
    },
    CHAT_GROUP_LENGHT({ commit }, { guid }) {
      return new Promise((resolve, reject) => {
        commit('POPUP_IS_FETCHING')
        let groupMembersRequest = new CometChat.GroupMembersRequestBuilder(guid).setLimit(30).build()

        groupMembersRequest.fetchNext().then(
          groupMembers => {
            commit('POPUP_IS_FETCHING', false)
            resolve(groupMembers.length)
          },
          error => {
            commit('POPUP_IS_FETCHING', false)
            reject(error)
          }
        )
      })
    }
  }
}
